/**
 * Map omni `/creators/*` URLs to paths on the Discovery SPA (iframe src path only).
 * Embeds always pass `logo=false` so the sidebar branding is hidden inside Omni.
 */

/** `location.state` when navigating to `/creators/*` so `DiscoveryEmbedPage` can return to the opening Omni screen. */
export type DiscoveryEmbedLocationState = {
  discoveryBackTo?: string;
};

export function omniPathToDiscoveryPath(pathname: string): string {
  const path = pathname.startsWith('/') ? pathname : `/${pathname}`;
  if (path === '/creators/lists/ambassadors' || path === '/creators/lists/ambassadors/') {
    return '/lists/ambassadors';
  }
  if (path === '/creators' || path === '/creators/') {
    return '/';
  }
  if (path === '/creators/search') {
    return '/search';
  }
  const creatorlistRe = /^\/creators\/creatorlist\/([^/]+)\/?$/;
  const cl = creatorlistRe.exec(path);
  if (cl) {
    return `/creatorlist/${decodeURIComponent(cl[1])}`;
  }
  const creatorRe = /^\/creators\/([^/]+)\/?$/;
  const cr = creatorRe.exec(path);
  if (cr) {
    const seg = cr[1];
    if (seg === 'search' || seg === 'creatorlist') {
      return '/';
    }
    return `/creator/${decodeURIComponent(seg)}`;
  }
  return '/';
}

/**
 * @param omniSearch - `location.search` from Omni (e.g. `?q=Messi`). Forwards `q` to Discovery `/search`
 * so header search and deep links prefill the embedded search.
 */
export function buildDiscoveryEmbedSrc(pathname: string, jwt: string, omniSearch?: string): string {
  const base = (import.meta.env.VITE_DISCOVERY_URL || '').replace(/\/$/, '');
  if (!base) {
    throw new Error('VITE_DISCOVERY_URL is not set');
  }
  const discPath = omniPathToDiscoveryPath(pathname);
  const url = new URL(base + discPath);
  url.searchParams.set('jwt', jwt);
  url.searchParams.set('logo', 'false');

  if (omniSearch && discPath === '/search') {
    const sp = new URLSearchParams(omniSearch.startsWith('?') ? omniSearch.slice(1) : omniSearch);
    const q = sp.get('q');
    if (q != null && q !== '') {
      url.searchParams.set('q', q);
    }
  }

  return url.toString();
}

/**
 * Iframe for a single creator: public profile summary only (audience interests, recent content, etc.),
 * without the full Discovery app chrome / side nav. Same URL shape as beatly-bo-app
 * `buildDiscoveryCreatorSummaryUrl`: `/creator/:id/public?embed=public&jwt=`.
 */
export function buildDiscoveryCreatorPublicEmbedSrc(creatorId: string, jwt: string): string {
  const base = (import.meta.env.VITE_DISCOVERY_URL || '').replace(/\/$/, '');
  if (!base) {
    throw new Error('VITE_DISCOVERY_URL is not set');
  }
  const id = creatorId.trim();
  if (!id) {
    throw new Error('Creator ID is required');
  }
  const url = new URL(`${base}/creator/${encodeURIComponent(id)}/public`);
  url.searchParams.set('embed', 'public');
  url.searchParams.set('jwt', jwt);
  return url.toString();
}
